import { currentAppPath, toBrowserPath } from '@shared/config';
import { clearUserState, type Role, type SessionState, sessionStore } from '@shared/session';

import { normalizeError } from './api-error';
import { requestImpersonation } from './auth';
import { expireSession } from './http-client';
import type { ImpersonateRequest, SessionResponse } from './session.endpoints';
import { applySession } from './session-apply';
import { type RefreshOutcome, refreshSession } from './token-refresh';

export interface SwitchIdentityOptions {
  /** 바꿀 역할. 없으면 세션의 활성 역할로 갱신한다(대리 로그인 종료) */
  targetRole?: Role;
  /** 새 신원의 토큰을 받는 방법. 기본은 토큰 갱신(`targetRole`) */
  acquire?: () => Promise<RefreshOutcome>;
  /** 전환 뒤 보여 줄 경로. 새 역할에 있는 경로여야 한다 */
  to: string;
  /** 주소만 바꾼다(라우트 트리는 내려가 있다). 기본은 history.replaceState */
  replaceLocation?: (path: string) => void;
}

/**
 * 하드 네비게이션 없이 신원(역할)을 바꾼다. 순서가 중요하다.
 * 1. 상태를 `booting` 으로 내린다 → 라우트 트리가 내려간다
 * 2. 목적 경로로 먼저 옮긴다 → 새 역할에 없는 경로의 NotFound 가 깜빡이지 않는다
 * 3. 사용자별 상태(쿼리 캐시 · store · 오버레이)를 모두 비운다
 * 4. 신원을 바꾼다(역할을 지정한 토큰 갱신)
 * 5. 라우트 트리가 `key = userId:role` 로 다시 마운트된다(AppRouter) → 컴포넌트 로컬 상태까지 버린다
 *
 * 대리 로그인(impersonation)의 시작 · 종료도 같은 순서를 따른다(토큰을 받는 엔드포인트만 다르다).
 */
export async function switchIdentity(options: SwitchIdentityOptions): Promise<RefreshOutcome> {
  const store = sessionStore.getState();
  const { status, accessToken, identity } = store;
  const before: SessionState = { status, accessToken, identity };
  const replaceLocation =
    options.replaceLocation ??
    ((path: string) => window.history.replaceState(null, '', toBrowserPath(path)));
  const beforePath = currentAppPath();

  store.setBooting();
  replaceLocation(options.to);

  const acquire =
    options.acquire ??
    (() => refreshSession(options.targetRole ? { targetRole: options.targetRole } : {}));
  const outcome = await acquire();

  if (outcome.result === 'ok') {
    // 새 신원의 화면이 옛 신원의 자료를 보지 않게 한다.
    clearUserState();
    return outcome;
  }

  if (outcome.result === 'rejected' && outcome.error.status === 401) {
    // 세션 자체가 끝났다.
    expireSession();
    return outcome;
  }

  // 역할 전환만 거절됐거나(403) 서버에 닿지 못했다. 전환 전 신원 · 주소로 되돌린다.
  replaceLocation(beforePath);
  store.restore(before);
  return outcome;
}

/**
 * 대리 로그인 시작(D3 · Admin → LP · PF). 대리 토큰을 받아 `switchIdentity` 순서로 신원을 바꾼다.
 * 대상은 회원(담당자) id 다 — LP · 포트폴리오 자체의 id 가 아니다.
 */
export function startImpersonation(
  target: ImpersonateRequest,
  options: Omit<SwitchIdentityOptions, 'targetRole' | 'acquire'>,
): Promise<RefreshOutcome> {
  return switchIdentity({
    ...options,
    acquire: async () => {
      try {
        applySession(await requestImpersonation(target));
        return { result: 'ok' };
      } catch (caught) {
        const error = normalizeError(caught);
        const rejected = error.status === 401 || error.status === 403 || error.status === 404;
        return { result: rejected ? 'rejected' : 'unreachable', error };
      }
    },
  });
}

/**
 * 대리 로그인 종료. 리프레시 쿠키는 대리인의 것이므로 갱신 한 번이면 대리인 신원으로 돌아온다(be.md B-4).
 */
export function endImpersonation(
  options: Omit<SwitchIdentityOptions, 'targetRole' | 'acquire'>,
): Promise<RefreshOutcome> {
  return switchIdentity(options);
}

/**
 * 새 세션(로그인)으로 들어간다. 라우터는 신원이 바뀌면 지금 주소로 새로 만들어지므로
 * 주소를 먼저 옮기고 → 사용자별 상태를 비우고 → 세션을 반영한다(`switchIdentity` 와 같은 순서).
 */
export function enterSession(
  data: SessionResponse,
  to: string,
  replaceLocation: (path: string) => void = (path) => window.history.replaceState(null, '', path),
): void {
  replaceLocation(to);
  clearUserState();
  applySession(data);
}
